<template>
    <Layout>
        <main :class="$style.main">
            <Breadcrumbs :items="pathCategories" />
            <div :class="$style.container">
                <CategoryHeader :category="categoryInfo" />
                <div :class="$style.pagesAndSeo">
                    <SeoText
                        v-if="categoryInfo?.seoText"
                        :content="categoryInfo.seoText"
                    />
                    <div :class="$style.pages">
                        <template
                            v-for="(page, pageIndex) in pages"
                            :key="pageIndex"
                        >
                            <div
                                v-if="Array.isArray(page) || showPlaceholders"
                                :id="`page-${pageIndex + 1}`"
                                :ref="pageDivRef"
                                :data-page-number="pageIndex + 1"
                            >
                                <div :class="$style.grid">
                                    <template v-if="Array.isArray(page)">
                                        <template
                                            v-for="(game, cardIndex) in page"
                                            :key="game.hru"
                                        >
                                            <GameCard
                                                data-interface-target="game"
                                                :game="game"
                                                appearance="medium"
                                                responsive
                                                :lazy="pageIndex === 0 ? cardIndex > 2 : true"
                                            />
                                        </template>
                                    </template>
                                    <template v-else>
                                        <template
                                            v-for="(_, idx) in placeholderPage"
                                            :key="`placeholder-${idx}`"
                                        >
                                            <GameCard
                                                :game="undefined"
                                                appearance="medium"
                                                responsive
                                            />
                                        </template>
                                    </template>
                                </div>
                            </div>
                            <PromoCategories
                                v-if="pageIndex === 1"
                                :class="$style.promo"
                            />
                        </template>
                        <div
                            v-if="showPlaceholders && nextPagePlaceholder"
                            :ref="pageDivRef"
                            :data-page-number="pages.length + 1"
                        >
                            <div :class="$style.grid">
                                <template
                                    v-for="(_, index) in placeholderPage"
                                    :key="`placeholder-${index}`"
                                >
                                    <GameCard
                                        :game="undefined"
                                        appearance="medium"
                                        responsive
                                    />
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
                <TheEnd />
            </div>
        </main>
    </Layout>
</template>
<script setup lang="ts">
import { onServerPrefetch, onMounted, computed, watch, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { ReactiveHead, useHead } from '@unhead/vue'
import { useIntersectionObserver } from '@vueuse/core'

import Layout from '@/layouts/ModernTwoColumnsLayout.vue'
import TheEnd from '@/components_new/TheEnd/TheEnd.vue'
import GameCard from '@/components_new/GameCard.vue'
import PromoCategories from '@/components_new/PromoCategories/PromoCategories.vue'
import { useCategoriesStore } from '@/store/categories-store'
import Breadcrumbs from '@/components_new/Breadcrumbs.vue'
import SeoText from '@/components_new/SeoText.vue'
import { useCategoryPageStore } from './category-page-store'
import CategoryHeader from './components/CategoryHeader.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { OLD_URLS_MAP } from '@/pages/Category/category.constants'
import { useSetPageData } from '@/utils/vue-hooks/use-set-page-data'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'
import { useAdsStore } from '@/store/ads-store'
import { useTr } from '@/composables/use-tr'
import { useUrl } from '@/composables/url-helpers'
import { useLocalizedRouters } from '@/composables/localized-routers'

const categoriesStore = useCategoriesStore()
const categoryPageStore = useCategoryPageStore()
const appStore = useAppStore()
const isMounted = useIsMounted()
const adsStore = useAdsStore()

const tr = useTr()

const { pages, pageSize, category, categoryInfo } = storeToRefs(categoryPageStore)
/* now we detect two statuses - when page is intersected and when it's fully viewed in a viewport
 * intersectedPageIndex refers to page that slightly shown and we can preload data for it to avoid placeholders
 * visiblePageIndex refers to url pagination - now we set next page only when previous page is fully hidden to avoid misleading pagination for big viewports
 *  */
const visiblePageIndex = ref(1)
const intersectedPageIndex = ref(1)
const placeholderPage = computed(() => Array(pageSize.value).fill(null))
const nextPagePlaceholder = computed(() => {
    if (category.value) {
        return pages.value.length < category.value.totalPages
    }
    return true
})

const $route = useRoute()
const router = useRouter()
const { getCategoryRoute } = useUrl()

const categoryHru = computed(() => $route.params.hru as string)
const pathCategories = computed(() => {
    const catPath = categoryInfo.value?.pathCategories || []
    const path = catPath.map((p, index) => ({
        ...p,
        disabled: index === catPath.length - 1,
    }))
    if (path) {
        return [
            {
                title: tr('categoryPage.main_title'),
                href: router.resolve({ name: ROUTE_NAMES.MAIN }).fullPath,
            },
            ...path,
        ]
    }
    return []
})

const pageDivRefs = ref<HTMLElement[]>([])
function pageDivRef(el: unknown | null) {
    if (el) {
        pageDivRefs.value.push(el as HTMLElement)
    } else {
        pageDivRefs.value = pageDivRefs.value.filter((div) => div !== el)
    }
}
useIntersectionObserver(pageDivRefs, (entries) => {
    entries.forEach(onPageIntersection)
})
function onPageIntersection({ isIntersecting, target }: IntersectionObserverEntry) {
    /* Sometimes this callback fires after the route has already changed, and the component is about to be unmount.
    So we check if it is still mounted to prevent unnecessary route changes.*/
    if (!isMounted.value) {
        return
    }
    const pageIndexString = (target as HTMLElement).dataset.pageNumber
    if (!pageIndexString) {
        return
    }
    const pageIndex = parseInt(pageIndexString, 10)
    if (!visiblePageIndex.value) {
        visiblePageIndex.value = pageIndex
    }
    if (isIntersecting) {
        intersectedPageIndex.value = pageIndex
    }

    if (!isIntersecting && visiblePageIndex.value === pageIndex) {
        visiblePageIndex.value = intersectedPageIndex.value
        router.replace({
            // pass optional path to prevent vue router from adding/removing final slash
            path: $route.path,
            query: {
                ...$route.query,
                page: visiblePageIndex.value > 1 ? visiblePageIndex.value : undefined,
            },
        })
    }
}

const fullTitle = computed(() => categoryInfo.value?.fullTitle || '')

const defaultMetaTitle = computed(() => {
    switch (pageType.value) {
        case 'tag':
            return tr('categoryPage.tags_title', { title: fullTitle.value })
        case ROUTE_NAMES.SERIES:
            return tr('categoryPage.series_title', { title: fullTitle.value })
        default:
            return tr('categoryPage.title', { title: fullTitle.value })
    }
})

const defaultMetaDescription = computed(() => {
    switch (pageType.value) {
        case 'tag':
            return tr('categoryPage.tags_description', { title: fullTitle.value })
        case ROUTE_NAMES.SERIES:
            return tr('categoryPage.series_description', { title: fullTitle.value })
        default:
            return tr('categoryPage.description', { title: fullTitle.value })
    }
})

const metaTitle = computed(() => categoryInfo.value?.metaTitle || defaultMetaTitle.value)
const metaDescription = computed(() => categoryInfo.value?.metaDescription || defaultMetaDescription.value)

function routeChanged(prefetchNextPage = false) {
    const page = intersectedPageIndex.value
    const mainRequest = categoryPageStore.setCategory({
        categoryName: categoryHru.value,
        page,
    })

    if (prefetchNextPage) {
        categoryPageStore.setCategory({
            categoryName: categoryHru.value,
            page: page + 1,
        })
    }

    return mainRequest
}

const pageType = computed(() => {
    switch ($route.name) {
        case ROUTE_NAMES.TAG:
            return 'tag'
        case ROUTE_NAMES.SERIES:
            return 'series'
        default:
            return 'category'
    }
})

const organizationSchema = computed(() => {
    const [firstWordOfTitle] = fullTitle.value.split(' ')
    const getCategoryCharCode = (word: string, index?: number) => {
        return word.charCodeAt(index ?? word.length - 1)
    }
    const categoryCharCode = getCategoryCharCode(firstWordOfTitle)
    // Rating by last letter of category first word title
    const getRating = () => (categoryCharCode % 4) / 10 + 4.2
    // RatingCount by last letter of category first word title
    const getRatingCount = () => {
        const timestamp = new Date().getTime()
        const msInDay = 86400000
        // take current dayNumber
        const dayNumber = Math.floor(timestamp / msInDay)
        // take getCategoryCharCode and multiple by number from categoryCharCode or 90 if categoryCharCode is 0
        let total = categoryCharCode * getCategoryCharCode(firstWordOfTitle, 0)
        // add everyday 4 new points more
        total += dayNumber * 4
        // got from ~ 88_000 to 91_000 points that changes every day by 4 points
        return total
    }
    const aggregateRating = categoryCharCode
        ? {
              '@type': 'AggregateRating',
              bestRating: 5,
              ratingValue: getRating(),
              ratingCount: getRatingCount(),
              worstRating: 1,
          }
        : {}
    return {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        applicationCategory: 'GameApplication',
        aggregateRating,
        name: metaTitle.value,
        abstract: metaDescription.value,
        offers: {
            '@type': 'Offer',
            price: 0,
            priceCurrency: 'USD',
        },
        operatingSystem: ['Windows', 'Chrome OS', 'Linux', 'MacOS', 'Android', 'iOS'],
    }
})

const { getHrefLangs } = useLocalizedRouters()

const pageMeta = computed(() => {
    if (!category.value || !category.value.id || !categoryInfo.value) {
        return {}
    }

    const links: ReactiveHead['link'] = [
        ...getHrefLangs({
            mapRoute(route, locale) {
                const localizedHru = categoryInfo.value?.localizations?.find((l) => l.lang === locale)?.hru
                if (!localizedHru) {
                    return route
                }
                return {
                    ...route,
                    params: {
                        ...route.params,
                        hru: localizedHru,
                    },
                    query: {
                        page: route.query?.page,
                    },
                }
            },
        }),
    ]

    if (visiblePageIndex.value > 1) {
        links.push({
            rel: 'prev',
            href: router.resolve({
                ...getCategoryRoute({ hru: categoryHru.value, type: category.value.categoryType }),
                query: { page: visiblePageIndex.value - 1 },
            }).href,
        })
    }
    if (visiblePageIndex.value < category.value.totalPages) {
        links.push({
            rel: 'next',
            href: router.resolve({
                ...getCategoryRoute({ hru: categoryHru.value, type: category.value.categoryType }),
                query: { page: visiblePageIndex.value + 1 },
            }).href,
        })
    }

    const pageStr =
        visiblePageIndex.value > 1 ? ` | ${tr('categoryPage.page', { page: String(visiblePageIndex.value) })}` : ''

    return {
        title: () => `${metaTitle.value}${pageStr}`,
        script: [
            {
                type: 'application/ld+json',
                innerHTML: organizationSchema.value,
            },
        ],
        meta: [
            {
                name: 'title',
                content: `${metaTitle.value}${pageStr}`,
            },
            {
                name: 'description',
                content: `${metaDescription.value}${pageStr}`,
            },
            {
                property: 'og:title',
                content: `${metaTitle.value}${pageStr}`,
            },
            {
                property: 'og:description',
                content: `${metaDescription.value}${pageStr}`,
            },
            {
                name: 'twitter:title',
                content: `${metaTitle.value}${pageStr}`,
            },
            {
                name: 'twitter:description',
                content: `${metaDescription.value}${pageStr}`,
            },
        ],
        link: links,
    }
})

useHead(pageMeta)

watch(
    () => categoryHru.value,
    () => routeChanged(true),
)

watch(
    () => intersectedPageIndex.value,
    () => routeChanged(true),
)

watch(
    () => categoryInfo.value?.id,
    () => {
        adsStore.updateTargeting({ content_category: categoryInfo.value?.id || '' })
    },
)

// TODO deduplicate with Game.vue
function processRedirectResponse() {
    const { categoryRedirect } = categoryPageStore
    if (!categoryRedirect) {
        return
    }
    const { redirectTarget, status: statusCode } = categoryRedirect

    let redirectURL = ''

    if (redirectTarget.type === 'game') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.GAME_PAGE,
            params: { game: redirectTarget.game_hru },
        }).href
    } else if (redirectTarget.type === 'category') {
        redirectURL = router.resolve(
            getCategoryRoute({ hru: redirectTarget.cat_hru, type: redirectTarget.cat_type }),
        ).href
    } else if (redirectTarget.type === 'main-page') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.MAIN,
        }).href
    }

    appStore.setPageStatus(statusCode)
    appStore.setTargetURL(redirectURL)
}

onServerPrefetch(async () => {
    const newUrl = OLD_URLS_MAP[`/${pageType.value}/${categoryHru.value}`]
    if (newUrl) {
        appStore.setPageStatus(301)
        appStore.setTargetURL(newUrl)
        return
    }
    await Promise.all([routeChanged(), categoriesStore.setMainData()])
    processRedirectResponse()
})

const showPlaceholders = ref(false)

useSetPageData(() => ({
    content_category: pageType.value === 'category' ? categoryInfo.value?.id || '' : '',
}))

onMounted(async () => {
    showPlaceholders.value = true
    intersectedPageIndex.value = parseInt($route.query.page as string, 10) || 1
    if (category.value?.id !== categoryHru.value) {
        routeChanged()
    }
    await categoriesStore.setMainData()
    processRedirectResponse()
})
</script>
<style module>
.container {
    --pages-gap: 8px;
}

.container,
.main,
.pagesAndSeo,
.pages {
    display: flex;
    flex-direction: column;
    gap: var(--pages-gap);
}

/*
    Style for empty Ad block.
    Cannot not be just "display: none" because Ad container needs to be visible to react on size change.
*/
.emptyAd {
    margin-top: calc(var(--pages-gap) * -1);
}

.pagesAndSeo,
.main {
    flex-direction: column-reverse;
}

.grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: dense;
    gap: 8px;
    --cards-gap: 8; /* unitless, consumed by GameCard component */
    position: relative;
}

.promo {
    margin: calc(24px - var(--pages-gap)) 0;
}

@media (--mobile-landscape) {
    .grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (--tablet-plus) {
    .grid {
        grid-template-columns: repeat(4, 1fr);
    }
    .tabletPlus {
        display: block;
    }
}

@media (--desktop-xlarge-plus) {
    .grid {
        grid-template-columns: repeat(6, 1fr);
    }
}
</style>
